/**
 * @file ButtonRadiusField.tsx
 * @description «Скругление кнопок» числом или «Пилюлей» (phase19.16, партия
 * SB120, этап 2; site-builder.md SB11, SB120; проект uiux-sb120-stage2 §2
 * «Кнопки и карточки», §10 вопрос 1 — «Пилюля» переключателем; данные
 * db-design-sb120-stage2 §1 `shapes.buttonRadius: number 0–40 | "pill"`):
 *
 * ```
 * [◖ Пилюля ◗]                     ← кнопка-переключатель, как «Во всю» у ширины блока
 * Скругление кнопок ├───●──────┤ [ 10 ] px   [Прямые 0][Скруглённые 10][Мягкие 20]
 * ```
 *
 * «Пилюля» включена — ползунок полый (серый) с подписью «пилюля»; движение
 * ползунка или ступень снимают «Пилюлю» и ставят число. Повторное нажатие
 * «Пилюли» возвращает «Скруглённые 10». Начало перетаскивания при «Пилюле»
 * сразу шлёт на канву число под бегунком (40), запись — по отпусканию.
 * Высоту кнопок (≥ 48) задаёт платформа.
 */

import { SITE_THEME_LIMITS, type SiteButtonRadius } from "@chaiberi/shared";

import { NumberSliderField } from "../../site-fields/NumberSliderField";
import { BUTTON_RADIUS_PRESETS } from "../../../lib/site-view/appearance-numbers";

const { buttonRadius: BR } = SITE_THEME_LIMITS;
/** Число после выключения «Пилюли» — прежняя «Скруглённая». */
const AFTER_PILL = 10;

interface Props {
  value: SiteButtonRadius;
  onChange: (value: SiteButtonRadius) => void;
  /** Живой предпросмотр во время перетаскивания (не запись). */
  onPreview?: (value: SiteButtonRadius) => void;
}

export function ButtonRadiusField({ value, onChange, onPreview }: Props) {
  const pill = value === "pill";
  // Тянут ползунок (не поле числа и не ступени) — показать на канве число под бегунком.
  const startDrag = (target: EventTarget) => {
    if (target instanceof Element && target.closest("[data-slot=\"slider\"]")) onPreview?.(BR.max);
  };
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <button
        type="button"
        aria-pressed={pill}
        onClick={() => onChange(pill ? AFTER_PILL : "pill")}
        className="inline-flex h-12 items-center gap-2 self-start rounded-lg bg-surface-low px-3 text-sm font-medium text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-ring aria-pressed:[box-shadow:inset_0_0_0_2px_var(--primary)] can-hover:h-8"
      >
        {/* Значок — сама форма «пилюли». */}
        <span aria-hidden className="h-3 w-6 rounded-full border-2 border-current" />
        Пилюля
      </button>
      {/* «Пилюля» + начало перетаскивания — предпросмотр числом сразу (QA SB120 этап 2: канва держала 9999px до отпускания). */}
      <div className="min-w-0" onPointerDownCapture={pill && onPreview ? (e) => startDrag(e.target) : undefined}>
        <NumberSliderField
          label="Скругление кнопок"
          value={pill ? undefined : value}
          inherit={pill ? { value: BR.max, label: "пилюля" } : { value: 0, label: "0 px" }}
          min={BR.min}
          max={BR.max}
          step={1}
          unit="px"
          display={{ factor: 1, decimals: 2 }}
          presets={BUTTON_RADIUS_PRESETS}
          hint={pill ? "Пилюля: скругление по высоте кнопки. Сдвиньте ползунок — будет число." : undefined}
          onChange={(v) => onChange(v ?? 0)}
          onPreview={onPreview ? (v) => onPreview(v ?? (pill ? "pill" : 0)) : undefined}
        />
      </div>
    </div>
  );
}
